"use client";

import * as Dialog from "@radix-ui/react-dialog";
import clsx from "clsx";
import Image from "next/image";
import { useState, type ReactNode } from "react";

import type { FragmentType } from "../../../../__generated__/gql";
import { graphql, useFragment } from "../../../../__generated__/gql";
import { Separator } from "../../../_components/separator";
import { Skeleton } from "../../../_components/skeleton";
import { Link } from "../../../_components/ui/link";

import {
  buildFacilityDetailContents,
  FacilityLabeledContents,
} from "./facility-labeled-contents";

const Fragment = graphql(`
  fragment FacilityDetailModalFragment on Facility {
    label
    name
    image {
      rawUrl
    }
    description
    detailUrl
    isReserveEnabled
    unitType
    reserveDeadlineHour
    reserveDeadlineDaysBefore
    resources {
      baseFeePolicies {
        unit
        unitFeeAmount
      }
    }
  }
`);

type Props = {
  facilityData: FragmentType<typeof Fragment>;
  /** クリックして開く trigger。未指定時は facility.label のボタン (予約フォーム既定の見た目) */
  trigger?: ReactNode;
};

export default function FacilityDetailModal({ facilityData, trigger }: Props) {
  const facility = useFragment(Fragment, facilityData);
  const contents = buildFacilityDetailContents(facility);
  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <Dialog.Root>
      <Dialog.Trigger asChild>
        {trigger ?? (
          <button
            // trigger 省略で呼ぶのは予約カレンダーの拠点ヘッダだけ
            data-analytics-id="reservation_calendar_opened_facility_detail"
            className="bg-sbeige text-skogecha rounded-sm px-[10px] py-[6px] text-[10px] font-medium hover:opacity-80"
          >
            {facility.label}
          </button>
        )}
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="sd-overlay h-dvh min-h-dvh">
          <Dialog.Content
            className={clsx(
              "sd-content w-[335px] px-[20px] py-[28px] md:w-[640px] md:p-[40px]",
              "flex max-h-[85vh] flex-col gap-y-[20px]",
            )}
          >
            <div className="shrink-0">
              <Dialog.Title className="font-notojp text-skogecha text-[18px] leading-none font-bold tracking-[0.72px]">
                {facility.name}
              </Dialog.Title>
            </div>

            <div className="flex-1 overflow-y-auto">
              <div className="flex flex-col gap-y-[20px]">
                <div className="relative aspect-3/2 w-full overflow-hidden">
                  {!imageLoaded && <Skeleton className="absolute inset-0" />}
                  <Image
                    src={facility.image.rawUrl}
                    alt={facility.name}
                    fill
                    className="scale-105 object-cover"
                    onLoad={() => setImageLoaded(true)}
                  />
                </div>
                <p className="text-skogecha text-[14px] leading-[1.8] font-medium tracking-[0.24px] whitespace-pre-line md:text-[16px]">
                  {facility.description}
                </p>

                <Separator />
                <FacilityLabeledContents contents={contents} />
                {facility.detailUrl && (
                  <div className="flex justify-start">
                    <Link
                      href={facility.detailUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-analytics-id="facility_clicked_external_detail"
                      className="flex flex-row items-center gap-x-[5px]"
                    >
                      <span className="text-skogecha text-[12px] font-medium tracking-[0.15px] underline md:text-[14px]">
                        詳細はこちら
                      </span>
                      <Image
                        src="/i/icon_question.svg"
                        alt="詳細"
                        width={16}
                        height={16}
                        className="shrink-0"
                      />
                    </Link>
                  </div>
                )}
              </div>
            </div>

            <div className="shrink-0">
              <Dialog.Close asChild>
                <button
                  // 閉じたことに分析上の意味が無い
                  data-analytics-ignore
                  className="btn-secondary text-skogecha h-[30px] w-full text-[10px] md:ml-auto md:h-[48px] md:w-auto md:px-[70px] md:text-[14px]"
                >
                  閉じる
                </button>
              </Dialog.Close>
            </div>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
